import { feature, featureCollection } from "@turf/turf";
import { FeatureCollection } from "geojson";
import { useMemo } from "react";

import { GqlPlannerV2TransportationMethod, GqlPlannerV2TravelType } from "~/__generated__";
import { ROUTE_LONG_BIKE_COLOR } from "~/config/statusColors";
import { formatSeconds } from "~/modules/app/util/formatters";
import GaiaMapDataLayer from "~/modules/map/components/GaiaMapDataLayer";
import { GaiaMapStyleLine } from "~/modules/map/components/GaiaMapStyleComposites/GaiaMapStyleLine";
import { IMG_MARKER_FIELDWORKER_BIKE, IMG_MARKER_FIELDWORKER_CAR } from "~/modules/map/mapStyles";
import { LONG_BIKE_ROUTE_THRESHOLD_SECONDS } from "~/modules/plannerV2/constants/travelMethods";

import { RoutesLayerV2Fragment } from "./fragments.generated";

interface RouteProperty {
    id: string;
    type: string;
    transportationMethod: string;
    durationInSeconds: number;
    color: string;
    iconImage: string;
    durationLabel: string;
}

type Props = {
    routes: RoutesLayerV2Fragment[];
};

const ROUTE_SOURCE_ID = "planner_routes_source";
const ROUTE_LINE_LAYER = "planner_routes_layer";

export const ROUTE_LINE_COLOR_BY_TYPE: Record<GqlPlannerV2TravelType, string> = {
    [GqlPlannerV2TravelType.Primary]: "#008ED5",
    [GqlPlannerV2TravelType.Secondary]: "#00B4A0",
    [GqlPlannerV2TravelType.Adjacent]: "#7B68EE",
};

const ROUTE_ICON_BY_TRANSPORTATION_METHOD: Record<GqlPlannerV2TransportationMethod, string> = {
    [GqlPlannerV2TransportationMethod.Bike]: IMG_MARKER_FIELDWORKER_BIKE,
    [GqlPlannerV2TransportationMethod.Car]: IMG_MARKER_FIELDWORKER_CAR,
    [GqlPlannerV2TransportationMethod.Walk]: "",
};

function isLongBikeRoute(route: RoutesLayerV2Fragment): boolean {
    return (
        route.transportationMethod === GqlPlannerV2TransportationMethod.Bike &&
        route.durationInSeconds > LONG_BIKE_ROUTE_THRESHOLD_SECONDS
    );
}

function useRoutesGeoJson(routes: RoutesLayerV2Fragment[]): FeatureCollection {
    return useMemo(() => {
        return featureCollection(
            routes.map(route => {
                const properties: RouteProperty = {
                    id: route.id,
                    type: route.type,
                    transportationMethod: route.transportationMethod,
                    durationInSeconds: route.durationInSeconds,
                    color: isLongBikeRoute(route) ? ROUTE_LONG_BIKE_COLOR : ROUTE_LINE_COLOR_BY_TYPE[route.type],
                    iconImage: ROUTE_ICON_BY_TRANSPORTATION_METHOD[route.transportationMethod],
                    durationLabel: formatSeconds(route.durationInSeconds),
                };
                return feature(route.routePolyline, properties);
            }),
        );
    }, [routes]);
}

export default function RoutesLayerV2({ routes }: Props) {
    const routesGeoJson = useRoutesGeoJson(routes);

    return (
        <>
            <GaiaMapDataLayer id={ROUTE_SOURCE_ID} data={routesGeoJson} />
            <GaiaMapStyleLine
                id={ROUTE_LINE_LAYER}
                source={ROUTE_SOURCE_ID}
                width={6}
                colorProperty="color"
                border={{
                    width: 1,
                    color: "#FFFFFF",
                }}
                dashArray={["case", ["==", ["get", "type"], "primary"], [1, 0], [1, 1]]}
                icons={{ imageProperty: "iconImage", labelProperty: "durationLabel" }}
            />
        </>
    );
}
